import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, CardFooter, Typography, Chip } from "@material-tailwind/react";
import persianNumberFormat from "../../../helper/PersianNumberFormat";
import { BsArrowUpShort, BsArrowDownShort } from "react-icons/bs";

const isNil = (v) => v === undefined || v === null;

const safeNumber = (v) => {
  if (isNil(v) || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const faToEn = v
      .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
      .replace(/[٬,]/g, "")
      .trim();
    const n = Number(faToEn);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function StatisticsCard({
  title,
  icon,
  value,
  prevValue,

  format = "number", // number | currency | raw
  unit = "ریال",
  dir = "rtl",

  // visuals
  accent = "blue", // blue | green | red | amber | cyan | indigo | blue-gray
  footerText = "مشابه ماه گذشته",
  showFooter = true,
  showTrend = true,
}) {
  const curr = useMemo(() => (format === "raw" ? value : safeNumber(value)), [value, format]);
  const prev = useMemo(() => safeNumber(prevValue), [prevValue]);

  const displayValue = useMemo(() => {
    if (format === "raw") return value;
    return persianNumberFormat(curr, true);
  }, [format, curr, value]);

  const { trend, diffAbs, diffPercent } = useMemo(() => {
    if (!showTrend) return { trend: "none", diffAbs: null, diffPercent: null };
    if (prev <= 0) {
      if (curr > 0) return { trend: "up", diffAbs: curr, diffPercent: null };
      return { trend: "none", diffAbs: 0, diffPercent: null };
    }
    const d = curr - prev;
    const p = Math.round((Math.abs(d) / prev) * 100);
    if (d > 0) return { trend: "up", diffAbs: d, diffPercent: p };
    if (d < 0) return { trend: "down", diffAbs: d, diffPercent: p };
    return { trend: "flat", diffAbs: 0, diffPercent: 0 };
  }, [showTrend, curr, prev]);

  const chip = useMemo(() => {
    if (!showTrend) return null;

    const color =
      prev <= 0 ? "blue-gray" : trend === "up" ? "green" : trend === "down" ? "red" : "blue-gray";

    const text =
      prev <= 0 ? "بدون داده قبلی" : trend === "flat" ? "بدون تغییر" : `${diffPercent}%`;

    return (
      <Chip
        size="sm"
        variant="ghost"
        color={color}
        value={
          <span className="flex items-center gap-1">
            {trend === "up" && prev > 0 && <BsArrowUpShort size={18} />}
            {trend === "down" && prev > 0 && <BsArrowDownShort size={18} />}
            {text}
          </span>
        }
        className="rounded-full font-medium"
      />
    );
  }, [showTrend, prev, trend, diffPercent]);

  // Keep icon consistent (size + color)
  const Icon = useMemo(() => {
    if (!React.isValidElement(icon)) return icon;
    const prevClass = icon.props?.className ?? "";
    return React.cloneElement(icon, {
      className: `${prevClass} h-5 w-5 text-white`.trim(),
    });
  }, [icon]);

  // Accent badge background
  const badgeBg =
    accent === "green"
      ? "bg-gradient-to-br from-green-500 to-green-700"
      : accent === "red"
        ? "bg-gradient-to-br from-red-500 to-red-700"
        : accent === "amber"
          ? "bg-gradient-to-br from-amber-500 to-amber-700"
          : accent === "cyan"
            ? "bg-gradient-to-br from-cyan-500 to-cyan-700"
            : accent === "indigo"
              ? "bg-gradient-to-br from-indigo-500 to-indigo-700"
              : accent === "blue-gray"
                ? "bg-gradient-to-br from-blue-gray-600 to-blue-gray-800"
                : "bg-gradient-to-br from-blue-500 to-blue-700";

  return (
    <Card
      dir={dir}
      className="
        rounded-2xl bg-white
        border border-blue-gray-100
        shadow-sm hover:shadow-md hover:-translate-y-[2px]
        transition-all
      "
    >
      <CardBody className="p-5">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`${badgeBg} grid h-10 w-10 place-items-center rounded-2xl shadow-sm`}>
              {Icon}
            </div>

            <div className="flex flex-col">
              <Typography className="text-sm font-semibold text-blue-gray-800">
                {title}
              </Typography>
            </div>
          </div>

          {chip}
        </div>

        {/* Value row */}
        <div className="mt-4 flex items-end justify-between">
          <div className="flex items-end gap-2">
            <Typography className="text-3xl font-extrabold text-blue-gray-900 tracking-tight leading-none">
              {displayValue}
            </Typography>
            {format === "currency" && unit ? (
              <Typography className="text-xs text-blue-gray-500 mb-1">
                {unit}
              </Typography>
            ) : null}
          </div>

          {/* Optional absolute diff (small, clean) */}
          {showTrend && prev > 0 ? (
            <Typography className="text-xs text-blue-gray-500">
              تغییر:{" "}
              <span
                className={
                  diffAbs === 0
                    ? "font-semibold text-blue-gray-700"
                    : diffAbs > 0
                      ? "font-semibold text-green-700"
                      : "font-semibold text-red-700"
                }
              >
                {persianNumberFormat(Math.abs(diffAbs ?? 0), true)}
              </span>
              {diffAbs === 0 ? "" : diffAbs > 0 ? " ↑" : " ↓"}
            </Typography>
          ) : null}
        </div>

        {/* Previous line */}
        {showTrend ? (
          <div className="mt-3 flex items-center justify-between">
            <Typography className="text-xs text-blue-gray-500">
              مقدار قبلی:{" "}
              <span className="font-semibold text-blue-gray-700">
                {persianNumberFormat(prev, true)}
              </span>
            </Typography>
            
          </div>
        ) : null}
      </CardBody>

      {/* Optional footer slot for custom content */}
      {showFooter ? (
        <CardFooter className="px-5 py-3 border-t border-blue-gray-50">
          <Typography className="text-xs text-blue-gray-500">{footerText}</Typography>
        </CardFooter>
      ) : null}
    </Card>
  );
}

StatisticsCard.propTypes = {
  title: PropTypes.node.isRequired,
  icon: PropTypes.node.isRequired,
  value: PropTypes.any,
  prevValue: PropTypes.any,

  format: PropTypes.oneOf(["number", "currency", "raw"]),
  unit: PropTypes.string,
  dir: PropTypes.oneOf(["rtl", "ltr"]),

  accent: PropTypes.oneOf(["blue", "green", "red", "amber", "cyan", "indigo", "blue-gray"]),
  footerText: PropTypes.node,
  showFooter: PropTypes.bool,
  showTrend: PropTypes.bool,
};

export default StatisticsCard;
